import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tag, FolderTree, Menu, MessageSquare, Store, Upload, Send, Megaphone, ArrowRight } from 'lucide-react';

export default function Dashboard({ selectedTeam }) {
  const navigate = useNavigate();
  const { teamFeatures, featuresLoading, isFeatureEnabled } = useOutletContext() || {};
  const [stats, setStats] = useState({
    brands: 0,
    categories: 0,
    menuItems: 0,
    messages: 0
  });
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedTeam) {
      loadDashboard();
    } else {
      setLoading(false);
    }
  }, [selectedTeam]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [teamRes, brandsRes, catsRes, itemsRes, msgsRes] = await Promise.all([
        axiosInstance.get(`/teams/${selectedTeam}`),
        axiosInstance.get(`/teams/${selectedTeam}/brands`),
        axiosInstance.get(`/teams/${selectedTeam}/categories`),
        axiosInstance.get(`/teams/${selectedTeam}/menu-items`),
        axiosInstance.get(`/teams/${selectedTeam}/messages`)
      ]);
      
      setTeam(teamRes.data);
      setStats({
        brands: brandsRes.data.length,
        categories: catsRes.data.length,
        menuItems: itemsRes.data.length,
        messages: msgsRes.data.length
      });
    } catch (err) {
      console.error('Failed to load dashboard', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if feature is enabled for a path
  const checkFeature = (path) => {
    if (!isFeatureEnabled) return true;
    return isFeatureEnabled(path);
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96 text-center shadow-xl border-0 overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <Store className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Lütfen bir mağaza seçin veya yeni bir mağaza oluşturun.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || featuresLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const statCards = [
    { title: 'Markalar', value: stats.brands, icon: Tag, color: 'from-blue-500 to-cyan-500' },
    { title: 'Kategoriler', value: stats.categories, icon: FolderTree, color: 'from-purple-500 to-pink-500' },
    { title: 'Menü Butonları', value: stats.menuItems, icon: Menu, color: 'from-orange-500 to-red-500' },
    { title: 'Mesajlar', value: stats.messages, icon: MessageSquare, color: 'from-green-500 to-teal-500' }
  ];

  const allQuickActions = [
    {
      title: 'Ürün Yükleme',
      description: 'Yeni ürünleri yükleyin ve Telegram kanallarınıza yayınlayın',
      icon: Upload,
      color: 'from-purple-500 to-pink-500',
      path: '/product-upload'
    },
    {
      title: 'Menü Gönder',
      description: 'Günlük menüyü hemen gönderin',
      icon: Send,
      color: 'from-blue-500 to-cyan-500',
      path: '/send-menu'
    },
    {
      title: 'Duyuru Yap',
      description: 'Özel duyuru ve kampanyalar paylaşın',
      icon: Megaphone,
      color: 'from-orange-500 to-red-500',
      path: '/announcement'
    }
  ];

  // Filter quick actions based on feature visibility
  const quickActions = allQuickActions.filter(action => checkFeature(action.path));

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">{team?.name}</p>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {statCards.map((stat, idx) => (
          <Card key={idx} className="shadow-lg hover:shadow-xl transition-all border-0 overflow-hidden rounded-2xl" data-testid={`stat-card-${stat.title}`}>
            <CardHeader className={`bg-gradient-to-br ${stat.color} text-white pb-3`}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-lg font-semibold">{stat.title}</CardTitle>
                <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" />
              </div>
            </CardHeader>
            <CardContent className="pt-4 sm:pt-6">
              <p className="text-3xl sm:text-4xl font-bold text-gray-800" data-testid={`stat-value-${stat.title}`}>{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hızlı Erişim */}
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">Hızlı Erişim</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {quickActions.map((action, idx) => (
            <Card 
              key={idx} 
              className="shadow-lg hover:shadow-2xl transition-all border-0 cursor-pointer group rounded-2xl overflow-hidden"
              onClick={() => navigate(action.path)}
            >
              <CardContent className="p-6">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <action.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-bold mb-2 text-gray-900">{action.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-4">{action.description}</p>
                <Button 
                  className={`w-full bg-gradient-to-r ${action.color} hover:opacity-90 text-white rounded-2xl group-hover:gap-3 transition-all`}
                >
                  Başla
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
